import { useNavigate } from 'react-router-dom';

import { ConfirmModal } from '@/shared/components/confirm-modal';
import { ABSOLUTE_ROUTES } from '@/shared/constants/routes';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { useAppDispatch } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { useMemo, useState } from 'react';
import { IMedication } from '@/shared/types/interfaces/medication';
import { getMedId, isC2Medication, isGLPMedication } from '@/shared/helpers/medications';
import { Alert } from '@/shared/ui';

interface IProps {
    deleteOnConfirm?: boolean;
    medications: (ITransferPrescriptions | IMedication)[];
    isFromAddMedication?: boolean;
    onClose?(): void;
    onConfirm?(deletedMedicationIds?: (string | number)[]): void;
}

const RestrictedMedicationPopup = ({ deleteOnConfirm, medications, isFromAddMedication, onConfirm, onClose }: IProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const c2Medications = useMemo(() => medications.filter(isC2Medication), [medications]);
    const glpMedications = useMemo(() => medications.filter(isGLPMedication), [medications]);

    const allRestrictedMedications = [...c2Medications, ...glpMedications];

    const isOnlyC2Medications = c2Medications.length === allRestrictedMedications.length;
    const isOnlyGLPMedications = glpMedications.length === allRestrictedMedications.length;
    const popupConfig = useMemo(() => {

        const config = {
            title: 'Some Prescriptions Can’t Be Filled Through MedBox',
            message: (
                <>
                    Some medications — like Schedule II controlled substances or temperature-sensitive drugs such as GLP-1s — can’t be shipped or included in
                    your MedBox.
                </>
            ),
            alert: (
                <span className="font-normal font-dmSans text-15 sm:text-16 leading-[137%]">
                    To ensure your safety and the proper handling of your prescriptions, it’s best to have <span className='font-dmSansBold font-bold'>these types of medications</span> filled at a <span className='font-dmSansBold font-bold'>local pharmacy</span>.
                </span>
            )
        };

        if (isOnlyC2Medications) {
            config.title = `Highly restricted controlled substance${c2Medications.length > 1 ? 's' : ''} detected`;
            config.message = (
                <>
                    {`${c2Medications.length > 1 ? 'These prescriptions fall' : 'This prescription falls'}`} into the <i>most restricted</i> class
                    of controlled drugs and can’t be included in your MedBox.
                </>);
        }

        if (isOnlyGLPMedications) {
            config.title = glpMedications.length > 1 ? 'Some Prescriptions Can’t Be Filled Through MedBox' : 'This prescription can’t be filled through MedBox';
            config.message = (
                <>
                    {`${glpMedications.length > 1
                        ? 'These medications are highly temperature-sensitive. Shipping may expose them to unsafe conditions that could compromise their safety and effectiveness.'
                        : 'This medication is highly temperature-sensitive. Shipping may expose it to unsafe conditions that could compromise its safety and effectiveness.'}`}
                </>
            );
        }

        return config;
    }, [c2Medications.length, glpMedications.length, isOnlyC2Medications, isOnlyGLPMedications]);


    const handleConfirm = async () => {
        if (!deleteOnConfirm) {
            onClose?.();
            return;
        }

        setLoading(true);

        const results = await Promise.allSettled(
            allRestrictedMedications.map(item => dispatch(CustomersController.removeTransferPrescription(item as ITransferPrescriptions)))
        );

        if (results.some(item => item.status !== 'fulfilled' || item.value.meta.requestStatus !== 'fulfilled')) {
            toast.error('Unable to remove restricted medications. Please try again');
        } else {
            onConfirm?.(allRestrictedMedications.map(getMedId));
        }

        setLoading(false);
    };

    const onCancel = () => {
        onClose?.();
        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.ADD_MEDICATION);
    };

    // const showTwoButtons = medications.length > allRestrictedMedications.length && !isFromAddMedication;
    const showTwoButtons =  !isFromAddMedication;
    // confirmText={showTwoButtons || !isFromAddMedication ? 'I Understand' : 'Add More Medication'}

    return (
        <ConfirmModal
            open
            cancelButtonHidden={!showTwoButtons}
            cancelText="Add more medication"
            confirmText='I Understand'
            cancelVariant="text"
            title={popupConfig.title}
            loading={loading}
            closeIcon
            onClose={onClose}
            onConfirm={handleConfirm}
            onCancel={onCancel}
            className="!max-w-[490px] px-[20px] py-[25px] sm:p-[40px]"
            titleClassName='mb-[0.9375rem] sm:mb-[1.25rem] font-25 sm:text-30 text-center font-agrandir leading-[140%]'
            childrenContainerClassName='mb-[1.25rem]'
            cancelFontClassName="font-dmSansBold font-bold text-15 sm:text-18 leading-[130%]"
            confirmClassName="font-dmSansMedium font-bold"
            iconClassName='mb-[0.625rem]'
        >
            <div className="flex flex-col items-center gap-[0.9375rem] sm:gap-[1.25rem]">
                <div className="flex flex-col gap-[0.625rem] w-full">
                    {allRestrictedMedications.map(item => (
                        <span
                            key={(item as ITransferPrescriptions).prescribable?.medid || item.medid}
                            className="ph-no-capture w-full flex items-center justify-center text-center font-dmSansBold font-bold leading-[150%] sm:leading-[139%] text-16 sm:text-18 bg-peach-600 rounded-lg px-[15px] py-[5px] min-h-[65px]"
                            data-testid="c2-medication-name"
                        >
                            {(item as ITransferPrescriptions).prescribable?.name || (item as IMedication).name}
                        </span>
                    ))}
                </div>

                <p className="w-full text-center font-dmSans font-normal text-18 sm:text-20 leading-[140%] sm:leading-[137%]">{popupConfig.message}</p>
                {popupConfig.alert &&
                    <Alert
                        className='py-[22px] pr-[12px] pl-[20px]'
                        label={popupConfig.alert}
                        variant="success"
                    />}
            </div>
        </ConfirmModal>
    );
};

export default RestrictedMedicationPopup;
